import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Monitor, Moon, Sun, X, type LucideIcon } from 'lucide-react';
import { api, type PublicAccount } from '../lib/api';
import {
  ACCENTS,
  ACCOUNT_PALETTE,
  DEFAULT_APPEARANCE,
  DEFAULT_DENSITY,
  DEFAULT_THEME,
  accentContrast,
  accentSwatch,
  useUiStore,
  type AccentName,
  type Appearance,
  type Density,
  type FontScale,
  type Measure,
  type ReadingLayout,
  type RowLines,
  type Theme,
} from '../lib/store';
import styles from './AppearanceSettings.module.css';

/**
 * The answer to "is it customizable?".
 *
 * Everything here writes straight through to the store, which persists to
 * localStorage and re-applies CSS custom properties on <html> — so every
 * control is live, with no reload and no preview/apply step. Per-account rail
 * colour is the one exception: that lives on the server, so it PATCHes.
 */

const THEMES: { value: Theme; label: string; icon: LucideIcon }[] = [
  { value: 'system', label: 'System', icon: Monitor },
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
];

const DENSITIES: { value: Density; label: string; hint: string }[] = [
  { value: 'compact', label: 'Compact', hint: '30px' },
  { value: 'cozy', label: 'Cozy', hint: '40px' },
  { value: 'comfortable', label: 'Comfortable', hint: '48px' },
];

const LINES: { value: RowLines; label: string }[] = [
  { value: 1, label: 'One line' },
  { value: 2, label: 'Two lines' },
];

const MEASURES: { value: Measure; label: string; hint: string }[] = [
  { value: 'narrow', label: 'Narrow', hint: '640' },
  { value: 'default', label: 'Default', hint: '720' },
  { value: 'wide', label: 'Wide', hint: '840' },
  { value: 'full', label: 'Full', hint: '∞' },
];

const READING_LAYOUTS: { value: ReadingLayout; label: string; hint: string }[] = [
  { value: 'full', label: 'Full', hint: 'Replaces the list · Esc goes back' },
  { value: 'right', label: 'Right', hint: 'Beside the list' },
  { value: 'bottom', label: 'Bottom', hint: 'Below the list' },
  { value: 'top', label: 'Top', hint: 'Above the list' },
];

const SCALES: { value: FontScale; label: string }[] = [
  { value: 90, label: '90%' },
  { value: 100, label: '100%' },
  { value: 110, label: '110%' },
];

/** Only the boolean members of Appearance can drive a switch. */
type BooleanAppearanceKey = {
  [K in keyof Appearance]: Appearance[K] extends boolean ? K : never;
}[keyof Appearance];

const TOGGLES: { key: BooleanAppearanceKey; label: string; hint: string }[] = [
  { key: 'showAvatars', label: 'Avatars', hint: 'Sender initials beside each row' },
  { key: 'showSnippets', label: 'Snippets', hint: 'The first line of the message body' },
  { key: 'showSplitTabs', label: 'Split tabs', hint: 'Category strip above the list · [ and ]' },
  {
    key: 'autoLoadImages',
    label: 'Load images',
    hint: 'Fetched by the server, so senders never see your IP',
  },
];

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className={styles.field}>
      <div className={styles.fieldHead}>
        <span className={styles.fieldLabel}>{label}</span>
        {hint && <span className={styles.fieldHint}>{hint}</span>}
      </div>
      {children}
    </div>
  );
}

/** A segmented control. `aria-pressed` rather than radios: these are commands. */
function Segmented<T extends string | number>({
  options,
  value,
  onChange,
  label,
}: {
  options: { value: T; label: string; hint?: string; icon?: LucideIcon }[];
  value: T;
  onChange: (next: T) => void;
  label: string;
}) {
  return (
    <div className={styles.segmented} role="group" aria-label={label}>
      {options.map((option) => {
        const Icon = option.icon;
        return (
          <button
            key={String(option.value)}
            type="button"
            className={styles.segment}
            data-active={option.value === value}
            aria-pressed={option.value === value}
            onClick={() => onChange(option.value)}
          >
            {Icon && <Icon size={14} className="lucide" aria-hidden="true" />}
            <span>{option.label}</span>
            {option.hint && <span className={styles.segmentHint}>{option.hint}</span>}
          </button>
        );
      })}
    </div>
  );
}

function Toggle({
  checked,
  label,
  hint,
  onChange,
}: {
  checked: boolean;
  label: string;
  hint: string;
  onChange: (next: boolean) => void;
}) {
  return (
    <button
      type="button"
      className={styles.toggle}
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
    >
      <span className={styles.toggleText}>
        <span className={styles.toggleLabel}>{label}</span>
        <span className={styles.toggleHint}>{hint}</span>
      </span>
      <span className={styles.track} data-on={checked} aria-hidden="true">
        <span className={styles.thumb} />
      </span>
    </button>
  );
}

export default function AppearanceSettings() {
  const open = useUiStore((s) => s.appearanceOpen);
  const setOpen = useUiStore((s) => s.setAppearanceOpen);
  if (!open) return null;
  return <AppearanceDialog onClose={() => setOpen(false)} />;
}

function AppearanceDialog({ onClose }: { onClose: () => void }) {
  const theme = useUiStore((s) => s.theme);
  const setTheme = useUiStore((s) => s.setTheme);
  const density = useUiStore((s) => s.density);
  const setDensity = useUiStore((s) => s.setDensity);
  const appearance = useUiStore((s) => s.appearance);
  const setAppearance = useUiStore((s) => s.setAppearance);
  const resetAppearance = useUiStore((s) => s.resetAppearance);

  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  // Owned locally rather than routed through the shell's Escape chain: this
  // dialog is the innermost layer whenever it is open.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  // The swatches have to be drawn in the theme the user is actually looking at,
  // not the one they picked — "System" is only resolvable against the OS.
  const dark =
    theme === 'dark' ||
    (theme === 'system' &&
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches);

  const isDefault =
    theme === DEFAULT_THEME &&
    density === DEFAULT_DENSITY &&
    (Object.keys(DEFAULT_APPEARANCE) as (keyof Appearance)[]).every(
      (k) => appearance[k] === DEFAULT_APPEARANCE[k],
    );

  return (
    <div
      className={styles.scrim}
      data-motion="overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-label="Appearance"
        data-motion="overlay"
      >
        <div className={styles.head}>
          <h2 className={styles.title}>Appearance</h2>
          <button
            type="button"
            className={styles.close}
            ref={closeRef}
            onClick={onClose}
            aria-label="Close appearance settings"
          >
            <X size={18} className="lucide" />
          </button>
        </div>

        <div className={styles.body}>
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Theme</h3>
            <Field label="Colour scheme">
              <Segmented label="Colour scheme" options={THEMES} value={theme} onChange={setTheme} />
            </Field>

            <Field label="Accent" hint="Rewrites the whole accent ramp from one hue">
              <div className={styles.swatches} role="group" aria-label="Accent colour">
                {ACCENTS.map((accent) => (
                  <button
                    key={accent.name}
                    type="button"
                    className={styles.swatch}
                    data-active={appearance.accent === accent.name}
                    aria-pressed={appearance.accent === accent.name}
                    aria-label={accent.label}
                    title={accent.label}
                    style={{
                      ['--swatch' as string]: accentSwatch(accent.name, dark),
                      ['--swatch-ink' as string]: accentContrast(accent.name),
                    }}
                    onClick={() => setAppearance('accent', accent.name as AccentName)}
                  >
                    {appearance.accent === accent.name && (
                      <Check size={13} className="lucide" aria-hidden="true" />
                    )}
                  </button>
                ))}
              </div>
            </Field>
          </section>

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>The list</h3>
            <Field label="Density" hint="Row height">
              <Segmented
                label="Density"
                options={DENSITIES}
                value={density}
                onChange={setDensity}
              />
            </Field>
            <Field label="Layout" hint="Subject and snippet beside the sender, or beneath it">
              <Segmented
                label="Row layout"
                options={LINES}
                value={appearance.rowLines}
                onChange={(v) => setAppearance('rowLines', v)}
              />
            </Field>
            <div className={styles.toggles}>
              {TOGGLES.map((t) => (
                <Toggle
                  key={t.key}
                  label={t.label}
                  hint={t.hint}
                  checked={appearance[t.key]}
                  onChange={(next) => setAppearance(t.key, next)}
                />
              ))}
            </div>
          </section>

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Reading</h3>
            <Field label="Measure" hint="Width of the message column">
              <Segmented
                label="Reading measure"
                options={MEASURES}
                value={appearance.measure}
                onChange={(v) => setAppearance('measure', v)}
              />
            </Field>
            <Field
              label="Reading pane"
              hint="Where a conversation opens. Split layouts fall back to Full on a narrow window."
            >
              <Segmented
                label="Reading pane"
                options={READING_LAYOUTS}
                value={appearance.readingLayout}
                onChange={(v) => setAppearance('readingLayout', v)}
              />
            </Field>
            <Field label="Text size" hint="Scales the whole interface">
              <Segmented
                label="Text size"
                options={SCALES}
                value={appearance.fontScale}
                onChange={(v) => setAppearance('fontScale', v)}
              />
            </Field>
          </section>

          <AiSection />

          <MailboxColours />

          <section className={styles.section}>
            <div className={styles.resetRow}>
              <div>
                <div className={styles.fieldLabel}>Reset to defaults</div>
                <div className={styles.fieldHint}>
                  System theme, cozy one-line rows, iris accent, 720px measure.
                </div>
              </div>
              <button
                type="button"
                className="kiln-button"
                disabled={isDefault}
                onClick={resetAppearance}
              >
                {isDefault ? 'Already default' : 'Reset'}
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

/** Only worth a row when the server actually has AI configured. */
function AiSection() {
  const showAi = useUiStore((s) => s.appearance.showAi);
  const setAppearance = useUiStore((s) => s.setAppearance);
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.me() });

  if (!me.data?.aiEnabled) return null;

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>AI</h3>
      <div className={styles.toggles}>
        <Toggle
          label="AI surfaces"
          hint="Thread summaries in the reading pane"
          checked={showAi}
          onChange={(next) => setAppearance('showAi', next)}
        />
      </div>
    </section>
  );
}

/**
 * The rails are the one thing this app does that Superhuman cannot: with eight
 * mailboxes in one list, hue *is* the account. Letting the user assign those
 * hues themselves is therefore the most valuable customisation on this panel.
 */
function MailboxColours() {
  const qc = useQueryClient();
  const accountsQuery = useQuery({ queryKey: ['accounts'], queryFn: () => api.accounts() });
  const accounts = useMemo(() => accountsQuery.data?.accounts ?? [], [accountsQuery.data]);
  const [failed, setFailed] = useState<number | null>(null);

  const patch = useMutation({
    mutationFn: ({ id, color }: { id: number; color: string }) =>
      api.updateAccount(id, { color }),
    onSuccess: () => {
      setFailed(null);
      void qc.invalidateQueries({ queryKey: ['accounts'] });
    },
    onError: (_error, variables) => setFailed(variables.id),
  });

  if (accounts.length === 0) return null;

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Mailbox colours</h3>
      <p className={styles.sectionBody}>
        The 3px rail on every row. Two mailboxes may share a hue, but the list
        stops being readable when they do.
      </p>

      {accounts.map((account: PublicAccount) => (
        <div key={account.id} className={styles.mailbox}>
          <span
            className={styles.mailboxRail}
            style={{ ['--rail' as string]: account.color }}
            aria-hidden="true"
          />
          <span className={styles.mailboxName} title={account.email}>
            {account.displayName}
          </span>
          <span className={styles.hues} role="group" aria-label={`Rail colour for ${account.email}`}>
            {ACCOUNT_PALETTE.map((hue) => {
              const active = account.color.toLowerCase() === hue.hex.toLowerCase();
              return (
                <button
                  key={hue.hex}
                  type="button"
                  className={styles.hue}
                  data-active={active}
                  aria-pressed={active}
                  aria-label={`${hue.label} for ${account.email}`}
                  title={hue.label}
                  style={{ ['--swatch' as string]: hue.hex }}
                  disabled={patch.isPending}
                  onClick={() => patch.mutate({ id: account.id, color: hue.hex })}
                />
              );
            })}
          </span>
          {failed === account.id && (
            <span className={styles.mailboxError} role="status">
              Could not save
            </span>
          )}
        </div>
      ))}
    </section>
  );
}
